import { FC } from 'react';

import { Errors, Loader } from '@/components';
import { findApplicableErrorDetails } from '@/components/common/Errors/utils';
import Heading, {
  HeadingTag,
  HeadingSize,
} from '@/components/ui/Heading/Heading';
import { BUTTON_STYLES } from '@/components/ui/Buttons/utils';

import { useErrorDirectives } from '@/context/ErrorDirectiveContext';

import { ERROR_KINDS } from '@/core/constants';
import { TypeProduct } from '@/core/generated-types/TypeProduct';

import useCustomerOrders from '@/lib/api/orderManager/hooks/useCustomerOrders';
import useDraftOrders from '@/lib/api/orderManager/hooks/useDraftOrders';

import { TypeDecoratedSubEngineSubscription } from '@/lib/api/subscriptionManager/decorators/subEngine/decorateSubEngineSubscriptions';
import { SubEngineSubscriptionStatusEnum } from '@/lib/api/subscriptionManager/types';

import { TypeAccountERCTranslations } from '@/utils/translations/types/TypeAccountERCTranslations';
import GenBanner, {
  BannerColorScheme,
  BannerBorderType,
} from '@/components/generics/GenBanner/GenBanner';

import { Box } from '../components/Box';
import HiddenUpcomingBoxes from '../components/HiddenUpcomingBoxes/HiddenUpcomingBoxes';
import NextOrder from '../components/NextOrder/NextOrder';

import { getErcBoxes } from '../utils';
import classNames from 'classnames';
import { hasBillingErrorInstallment } from '@/lib/api/subscriptionManager/helpers/subscriptionHelpers';

export type LayoutActiveErcSubscriptionProps = {
  t: TypeAccountERCTranslations;
  selectedSubscription: TypeDecoratedSubEngineSubscription;
  contentfulProductsObject: Record<string, TypeProduct>;
};

const LayoutActiveErcSubscription: FC<LayoutActiveErcSubscriptionProps> = ({
  t,
  selectedSubscription,
  contentfulProductsObject,
}) => {
  const { errorDirectives } = useErrorDirectives();

  const { errors, heading } = findApplicableErrorDetails(
    errorDirectives,
    ERROR_KINDS.DEFAULT,
  );

  const { customerOrdersData, customerOrdersLoading, customerOrdersError } =
    useCustomerOrders(selectedSubscription.user_uuid);

  const { draftOrdersData, draftOrdersLoading, draftOrdersError } =
    useDraftOrders(selectedSubscription.user_uuid, selectedSubscription.id);

  if (customerOrdersLoading || draftOrdersLoading) {
    return (
      <div data-testid="layout-active-subscription">
        <Loader loading text={t.loading} />
      </div>
    );
  }

  if (customerOrdersError || draftOrdersError) {
    return (
      <div data-testid="layout-active-subscription">
        <Errors errors={errors} heading={heading} />
      </div>
    );
  }

  const orders = customerOrdersData?.orders;

  const draftOrder = draftOrdersData?.draft_orders[0];

  const { currentBox, nextOrder, upcomingBoxes } = getErcBoxes({
    subscription: selectedSubscription,
    orders,
    draftOrder,
    contentfulProductsObject,
  });

  const visibleUpcomingBoxes = upcomingBoxes.slice(0, 3);

  const hiddenUpcomingBoxes = upcomingBoxes.slice(3);

  // Check if any installment has billing error OR subscription is suspended
  const isSuspended =
    selectedSubscription.status === SubEngineSubscriptionStatusEnum.SUSPENDED;
  const hasBillingError = hasBillingErrorInstallment(selectedSubscription);

  const updatePaymentMethodLink = isSuspended ? 'reactivate' : 'manage-payment';
  const reactivateSubscriptionLink = `/account/early-reader-club/${selectedSubscription.id}/${updatePaymentMethodLink}`;

  return (
    <div className="grid gap-y-10" data-testid="layout-active-subscription">
      {/* Current Box */}
      <div className="grid gap-y-2 md:gap-y-4">
        <Heading
          tag={HeadingTag.H1}
          size={HeadingSize.H1}
          classes="px-6 xl:px-0"
          text={t.currentBox}
        />

        {currentBox.orderData && (
          <Box
            t={t}
            orderData={currentBox.orderData}
            installmentData={currentBox.installmentData}
            productData={currentBox.productData}
            isCurrentBox
          />
        )}
      </div>

      {/* Subscription Notice Banner */}
      {(isSuspended || hasBillingError) && (
        <div className="mb-6 max-w-[900px] px-6 lg:px-0">
          <GenBanner
            testId="billing-error-banner"
            colorScheme={BannerColorScheme.ERROR}
            borderType={BannerBorderType.SOLID}
            noWrapCta={true}
            paragraphText={
              isSuspended
                ? t.paymentFailedSubscriptionMessageSuspended
                : t.paymentFailedSubscriptionMessage
            }
            ctaAttr={{
              href: reactivateSubscriptionLink,
              cta: isSuspended
                ? t.updatePaymentMethodSuspended
                : t.updatePaymentMethod,
              style: BUTTON_STYLES.link,
            }}
          />
        </div>
      )}

      <div
        className={classNames({
          'opacity-50 pointer-events-none': isSuspended || hasBillingError,
        })}
      >
        {/* Next Order */}
        <NextOrder t={t} nextOrder={nextOrder} />

        {/* Upcoming Boxes */}
        <div className="grid gap-y-4 md:gap-y-6">
          <Heading
            tag={HeadingTag.H1}
            size={HeadingSize.H1}
            classes="px-6 xl:px-0 mt-8"
            text={t.upcomingBoxes}
          />

          {visibleUpcomingBoxes.map((upcomingBox) => (
            <Box
              key={upcomingBox.installmentData.id}
              t={t}
              installmentData={upcomingBox.installmentData}
              productData={upcomingBox.productData}
              isUpcomingBox
            />
          ))}

          <HiddenUpcomingBoxes t={t} upcomingBoxes={hiddenUpcomingBoxes} />
        </div>
      </div>
    </div>
  );
};

export default LayoutActiveErcSubscription;
